import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateWebProfile, useUpdateWebProfileName, useWebProfile } from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@tark/shared/components';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  Save,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import {
  checkWebProfileName,
  cn,
  getErrorMessage,
  type UpdateWebProfilePayload,
  type WebUserProfile,
} from '@tark/shared/lib';

const PROFILE_NAME_PATTERN = /^[a-z0-9_]{3,15}$/;

const AGE_RANGE_OPTIONS = [
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const SPORT_OPTIONS = [
  { value: 'tennis', label: 'Tennis' },
  { value: 'pickleball', label: 'Pickleball' },
  { value: 'both', label: 'Both' },
];

const HANDEDNESS_OPTIONS = [
  { value: 'right', label: 'Right' },
  { value: 'left', label: 'Left' },
];

const BACKHAND_OPTIONS = [
  { value: 'one_handed', label: 'One-handed' },
  { value: 'two_handed', label: 'Two-handed' },
];

const PLAY_STYLE_OPTIONS = [
  { value: 'singles', label: 'Singles' },
  { value: 'doubles', label: 'Doubles' },
  { value: 'both', label: 'Both' },
];

type ProfileFormState = {
  firstName: string;
  lastName: string;
  profileName: string;
  zipCode: string;
  ageRange: string;
  gender: string;
  sport: string;
  handedness: string;
  backhand: string;
  playStyle: string;
  ustaRank: string;
  utrRank: string;
};

type ProfileField = keyof ProfileFormState;
type FormErrors = Partial<Record<ProfileField | 'form', string>>;

const PROFILE_FIELDS: ProfileField[] = [
  'firstName',
  'lastName',
  'profileName',
  'zipCode',
  'ageRange',
  'gender',
  'sport',
  'handedness',
  'backhand',
  'playStyle',
  'ustaRank',
  'utrRank',
];

const GENERAL_FIELDS: Exclude<ProfileField, 'profileName'>[] = [
  'firstName',
  'lastName',
  'zipCode',
  'ageRange',
  'gender',
  'sport',
  'handedness',
  'backhand',
  'playStyle',
  'ustaRank',
  'utrRank',
];

const EMPTY_FORM: ProfileFormState = {
  firstName: '',
  lastName: '',
  profileName: '',
  zipCode: '',
  ageRange: '',
  gender: '',
  sport: '',
  handedness: '',
  backhand: '',
  playStyle: '',
  ustaRank: '',
  utrRank: '',
};

export default function EditProfilePage() {
  const navigate = useNavigate();
  const profileQuery = useWebProfile();
  const updateProfile = useUpdateWebProfile();
  const updateProfileName = useUpdateWebProfileName();

  const [form, setForm] = useState<ProfileFormState>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<ProfileFormState | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [profileNameAvailable, setProfileNameAvailable] = useState<boolean | null>(null);
  const [checkingProfileName, setCheckingProfileName] = useState(false);

  const profile = profileQuery.data;
  const profileNameChanged = Boolean(initialForm && form.profileName !== initialForm.profileName);
  const profileNameLocked = Boolean(initialForm?.profileName && (profile?.profileNameDaysRemaining ?? 0) > 0);
  const hasChanges = Boolean(initialForm && PROFILE_FIELDS.some((field) => form[field] !== initialForm[field]));
  const hasGeneralChanges = Boolean(initialForm && GENERAL_FIELDS.some((field) => form[field] !== initialForm[field]));
  const isSaving = updateProfile.isPending || updateProfileName.isPending;
  const canSave =
    hasChanges &&
    !isSaving &&
    !checkingProfileName &&
    !(profileNameChanged && profileNameLocked) &&
    (!profileNameChanged || profileNameAvailable !== false);

  useEffect(() => {
    if (!profile || initialForm) {
      return;
    }

    const nextForm = toFormState(profile);
    setForm(nextForm);
    setInitialForm(nextForm);
  }, [initialForm, profile]);

  useEffect(() => {
    if (!initialForm) {
      return;
    }

    const nextProfileName = form.profileName.trim();
    setProfileNameAvailable(null);

    if (!profileNameChanged) {
      setProfileNameAvailable(true);
      setCheckingProfileName(false);
      return;
    }

    if (profileNameLocked || !PROFILE_NAME_PATTERN.test(nextProfileName)) {
      setCheckingProfileName(false);
      return;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      setCheckingProfileName(true);
      try {
        const result = await checkWebProfileName(nextProfileName);
        if (active) {
          setProfileNameAvailable(result.valid && result.available);
        }
      } catch {
        if (active) {
          setProfileNameAvailable(null);
        }
      } finally {
        if (active) {
          setCheckingProfileName(false);
        }
      }
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [form.profileName, initialForm, profileNameChanged, profileNameLocked]);

  const setField = (field: ProfileField, rawValue: string) => {
    const value = normalizeFieldValue(field, rawValue);
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      delete next.form;
      return next;
    });
  };

  const handleSave = async () => {
    if (!initialForm) {
      return;
    }

    const nextErrors = validateForm(form, profileNameLocked, profileNameChanged);
    if (profileNameChanged && profileNameAvailable === false) {
      nextErrors.profileName = 'That profile name is already taken.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setErrors({});

      if (hasGeneralChanges) {
        await updateProfile.mutateAsync(toUpdatePayload(form));
      }

      if (profileNameChanged) {
        await updateProfileName.mutateAsync(form.profileName.trim());
      }

      navigate('/profile');
    } catch (error) {
      setErrors({ form: resolveEditProfileErrorMessage(error) });
    }
  };

  if (profileQuery.isLoading) {
    return (
      <div className="mx-auto flex min-h-[420px] max-w-4xl items-center justify-center pb-24 md:pb-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (profileQuery.isError) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 pb-24 md:pb-8">
        <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/profile')}>
          Profile
        </Button>
        <Card variant="default">
          <CardContent className="space-y-4">
            <p className="font-semibold text-text">Edit profile unavailable</p>
            <p className="text-sm text-text-muted">We could not load your profile details right now.</p>
            <Button variant="outline" onClick={() => profileQuery.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-24 md:pb-8">
      <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/profile')}
          >
            Profile
          </Button>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-text">Edit profile</h1>
        </div>
        <Button
          leftIcon={<Save className="h-4 w-4" />}
          onClick={handleSave}
          disabled={!canSave}
          isLoading={isSaving}
          className="sm:self-end"
        >
          Save changes
        </Button>
      </div>

      {errors.form ? (
        <div className="rounded-3xl border border-red-200 bg-red-50/80 p-4 text-sm font-medium text-red-700">
          {errors.form}
        </div>
      ) : null}

      <Card variant="glass">
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={form.firstName}
              onChange={(event) => setField('firstName', event.target.value)}
              error={errors.firstName}
              autoComplete="given-name"
            />
            <Input
              label="Last name"
              value={form.lastName}
              onChange={(event) => setField('lastName', event.target.value)}
              error={errors.lastName}
              autoComplete="family-name"
            />
          </div>

          <Input
            label="Profile name"
            value={form.profileName}
            onChange={(event) => setField('profileName', event.target.value)}
            error={errors.profileName}
            disabled={profileNameLocked}
            autoComplete="off"
            rightIcon={
              checkingProfileName ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : profileNameChanged && profileNameAvailable === true ? (
                <CheckCircle2 className="h-4 w-4 text-primary" />
              ) : profileNameChanged && profileNameAvailable === false ? (
                <XCircle className="h-4 w-4 text-red-500" />
              ) : null
            }
            hint={getProfileNameHint({
              locked: profileNameLocked,
              daysRemaining: profile?.profileNameDaysRemaining ?? 0,
              changed: profileNameChanged,
              available: profileNameAvailable,
            })}
          />

          <Input
            label="ZIP code"
            value={form.zipCode}
            onChange={(event) => setField('zipCode', event.target.value)}
            error={errors.zipCode}
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
          />
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader>
          <CardTitle>About you</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <ChoiceSection
            label="Age range"
            value={form.ageRange}
            options={AGE_RANGE_OPTIONS}
            onChange={(value) => setField('ageRange', value)}
          />
          <ChoiceSection
            label="Gender"
            value={form.gender}
            options={GENDER_OPTIONS}
            onChange={(value) => setField('gender', value)}
          />
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader>
          <CardTitle>Sport profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <ChoiceSection
            label="Primary sport"
            value={form.sport}
            options={SPORT_OPTIONS}
            onChange={(value) => setField('sport', value)}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            <ChoiceSection
              label="Handedness"
              value={form.handedness}
              options={HANDEDNESS_OPTIONS}
              onChange={(value) => setField('handedness', value)}
            />
            <ChoiceSection
              label="Backhand"
              value={form.backhand}
              options={BACKHAND_OPTIONS}
              onChange={(value) => setField('backhand', value)}
            />
          </div>
          <ChoiceSection
            label="Play style"
            value={form.playStyle}
            options={PLAY_STYLE_OPTIONS}
            onChange={(value) => setField('playStyle', value)}
          />
          {(form.sport === 'tennis' || form.sport === 'both') ? (
            <div className="space-y-3 border-t border-border pt-5">
              <div>
                <p className="text-sm font-medium text-text">Tennis ratings</p>
                <p className="mt-1 text-xs text-text-muted">Optional and self-reported. Leave blank if not rated.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="NTRP"
                  value={form.ustaRank}
                  onChange={(event) => setField('ustaRank', event.target.value)}
                  error={errors.ustaRank}
                  placeholder="e.g. 3.5"
                  inputMode="decimal"
                  autoComplete="off"
                />
                <Input
                  label="UTR"
                  value={form.utrRank}
                  onChange={(event) => setField('utrRank', event.target.value)}
                  error={errors.utrRank}
                  placeholder="e.g. 6.25"
                  inputMode="decimal"
                  autoComplete="off"
                />
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 rounded-3xl border border-border bg-surface-elevated/30 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="text-sm text-text-muted">
            Phone and email stay under account verification. This page updates your public playing profile.
          </p>
        </div>
        <Button variant="outline" onClick={handleSave} disabled={!canSave} isLoading={isSaving}>
          Save
        </Button>
      </div>
    </div>
  );
}

function ChoiceSection({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-3 text-sm font-semibold text-text">{label}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(selected ? '' : option.value)}
              className={cn(
                'flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm transition-all',
                selected
                  ? 'border-primary bg-primary/10 font-semibold text-primary ring-2 ring-primary/20'
                  : 'border-border bg-surface-elevated/25 text-text hover:border-primary/40 hover:bg-surface-elevated'
              )}
              aria-pressed={selected}
            >
              <span>{option.label}</span>
              {selected ? (
                <span className="rounded-full bg-primary p-1 [color:var(--color-on-primary)]">
                  <Check className="h-3 w-3" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function toFormState(profile: WebUserProfile): ProfileFormState {
  return {
    firstName: profile.firstName?.trim() ?? '',
    lastName: profile.lastName?.trim() ?? '',
    profileName: profile.profileName?.trim() ?? '',
    zipCode: profile.zipCode?.trim() ?? '',
    ageRange: profile.ageRange?.trim() ?? '',
    gender: profile.gender?.trim() ?? '',
    sport: profile.sport?.trim() ?? '',
    handedness: profile.handedness?.trim() ?? '',
    backhand: profile.backhand?.trim() ?? '',
    playStyle: profile.playStyle?.trim() ?? '',
    ustaRank: profile.ustaRank?.trim() ?? '',
    utrRank: profile.utrRank?.trim() ?? '',
  };
}

function toUpdatePayload(form: ProfileFormState): UpdateWebProfilePayload {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    zipCode: form.zipCode.trim(),
    ageRange: form.ageRange,
    gender: form.gender,
    sport: form.sport,
    handedness: form.handedness,
    backhand: form.backhand,
    playStyle: form.playStyle,
    ustaRank: form.ustaRank.trim(),
    utrRank: form.utrRank.trim(),
  };
}

function normalizeFieldValue(field: ProfileField, value: string): string {
  if (field === 'profileName') {
    return value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 15);
  }

  if (field === 'zipCode') {
    return value.replace(/\D/g, '').slice(0, 5);
  }

  if (field === 'firstName' || field === 'lastName') {
    return value.slice(0, 50);
  }

  if (field === 'ustaRank' || field === 'utrRank') {
    const decimalPlaces = field === 'ustaRank' ? 1 : 2;
    const normalized = value.replace(',', '.').replace(/[^\d.]/g, '');
    const [whole = '', ...fractionParts] = normalized.split('.');
    if (fractionParts.length === 0) {
      return whole.slice(0, 2);
    }
    return `${whole.slice(0, 2)}.${fractionParts.join('').slice(0, decimalPlaces)}`;
  }

  return value;
}

function validateForm(
  form: ProfileFormState,
  profileNameLocked: boolean,
  profileNameChanged: boolean
): FormErrors {
  const nextErrors: FormErrors = {};
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const profileName = form.profileName.trim();
  const zipCode = form.zipCode.trim();

  if (firstName.length < 2) {
    nextErrors.firstName = 'Enter at least 2 characters.';
  }

  if (lastName.length < 2) {
    nextErrors.lastName = 'Enter at least 2 characters.';
  }

  if (profileNameChanged && profileNameLocked) {
    nextErrors.profileName = 'Profile name is locked right now.';
  } else if (!PROFILE_NAME_PATTERN.test(profileName)) {
    nextErrors.profileName = 'Use 3-15 lowercase letters, numbers, or underscore.';
  }

  if (zipCode && !/^\d{5}$/.test(zipCode)) {
    nextErrors.zipCode = 'Enter a valid 5-digit ZIP.';
  }

  if (form.sport === 'tennis' || form.sport === 'both') {
    const ntrp = form.ustaRank.trim();
    const ntrpValue = Number(ntrp);
    if (ntrp && (!/^\d(?:\.\d)?$/.test(ntrp) || ntrpValue < 1.5 || ntrpValue > 7 || !Number.isInteger(ntrpValue * 2))) {
      nextErrors.ustaRank = 'Use 1.5 to 7.0 in 0.5 steps.';
    }

    const utr = form.utrRank.trim();
    const utrValue = Number(utr);
    if (utr && (!/^\d{1,2}(?:\.\d{1,2})?$/.test(utr) || utrValue < 1 || utrValue > 16.5)) {
      nextErrors.utrRank = 'Use 1.0 to 16.5 with up to 2 decimals.';
    }
  }

  return nextErrors;
}

function getProfileNameHint({
  locked,
  daysRemaining,
  changed,
  available,
}: {
  locked: boolean;
  daysRemaining: number;
  changed: boolean;
  available: boolean | null;
}): string {
  if (locked) {
    return `You can change this again in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`;
  }

  if (changed && available === true) {
    return 'Available.';
  }

  if (changed && available === false) {
    return 'Already taken.';
  }

  return '3-15 lowercase letters, numbers, or underscore.';
}

function resolveEditProfileErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: unknown; error?: unknown } } }).response;
    if (typeof response?.data?.message === 'string') {
      return response.data.message;
    }
    if (typeof response?.data?.error === 'string') {
      return response.data.error;
    }
  }

  return getErrorMessage(error);
}
